import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useVersionInfo } from '../context/VersionContext';

interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
  showAll?: boolean;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed; inset:0; background:rgba(0,0,0,0.55); backdrop-filter:blur(8px) saturate(140%); z-index:1400;
  display:${p=>p.$open?'flex':'none'}; align-items:center; justify-content:center; animation:${fadeIn} .25s ease;
`;
const Modal = styled.div`
  background: linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.06)) , var(--theme-card-background,#ffffff);
  color:var(--theme-text,#222); width:min(680px,94vw);
  max-height:85vh; border-radius:26px; padding:40px 38px 42px; box-shadow:0 24px 68px -12px rgba(0,0,0,0.55),0 4px 12px rgba(0,0,0,.35);
  overflow-y:auto; animation:${slideUp} .32s cubic-bezier(.22,1.12,.32,1); position:relative;
  border:1px solid rgba(255,255,255,0.22);
`;
const Title = styled.h2`margin:0 0 4px;font-size:22px;`;
const Sub = styled.div`font-size:13px; opacity:.75; margin-bottom:18px;`;
const CloseBtn = styled.button`
  position:absolute; top:10px; right:10px; background:transparent; border:none; font-size:30px;
  cursor:pointer; line-height:1; color:var(--theme-text,#444);
`;
const List = styled.ul`margin:12px 0 0 20px; padding:0; line-height:1.4; font-size:14px;`;
const Footer = styled.div`margin-top:22px; display:flex; justify-content:flex-end; gap:12px; flex-wrap:wrap;`;
const Btn = styled.button`
  background:linear-gradient(135deg,var(--theme-primary,#2563eb),var(--theme-secondary,#1d4ed8));
  color:#fff; border:none; padding:12px 20px; border-radius:14px; font-weight:600;
  cursor:pointer; font-size:14px; display:inline-flex; align-items:center; gap:6px; letter-spacing:.3px;
  box-shadow:0 4px 14px -2px rgba(0,0,0,0.4);
  transition:transform .18s ease, box-shadow .25s ease, filter .25s ease;
  &:hover{transform:translateY(-3px); box-shadow:0 8px 22px -4px rgba(0,0,0,0.55);}
  &:active{transform:translateY(-1px) scale(.97);}
`;

export const CHANGELOG_EVENT = 'open-changelog-full';

const ChangelogModal: React.FC<ChangelogModalProps> = ({ open, onClose, showAll }) => {
  const { info, loading, error, refresh } = useVersionInfo();
  const [refreshing,setRefreshing] = React.useState(false);
  const doRefresh = async () => {
    setRefreshing(true);
    try { await refresh(); } finally { setTimeout(()=>setRefreshing(false), 400); }
  };
  const changes = info?.changes || [];
  const visibleChanges = showAll ? changes : changes.slice(0, 15);

  return (
    <Overlay $open={open} onClick={onClose}>
      <Modal onClick={e=>e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Changelog">
        <CloseBtn aria-label="Stäng" onClick={onClose}>×</CloseBtn>
        <Title>Nytt i version {info?.version || ''}</Title>
        <Sub>{loading ? 'Laddar...' : error ? error : info?.date ? new Date(info.date).toLocaleString() : ''}</Sub>
        {visibleChanges.length > 0 && (
          <List>
            {visibleChanges.map((c,i)=>(<li key={i}>{c}</li>))}
          </List>
        )}
        {(!loading && changes.length === 0) && <div>Inga ändringar listade.</div>}
        <Footer>
          <Btn disabled={refreshing} onClick={(e)=>{e.stopPropagation(); if(!refreshing) doRefresh();}}>{refreshing? '⏳...':'Uppdatera'}</Btn>
          <Btn onClick={(e)=>{e.stopPropagation(); onClose();}}>OK</Btn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ChangelogModal;
